import { useState } from "react";
import { useHomeownerAuth } from "./useHomeownerAuth";
import HomeownerGate from "./HomeownerGate";
import { authTriple, hoCreateReferral } from "../../lib/homeownerRpc";

export default function ReferralPage() {
  const auth = useHomeownerAuth();
  const { deal } = auth;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal || !name.trim() || !phone.trim()) return;
    setSubmitting(true);
    setErr(null);

    try {
      // referral-submit writes the structured referral AND texts the RM(s),
      // TL and ASM on this job. Previously this wrote a freetext blob and
      // texted the raw string to the office line, where it went nowhere.
      await hoCreateReferral(authTriple(deal), {
        name: name.trim(),
        phone: phone.trim(),
        street: address.trim(),
        city: city.trim(),
        notes: notes.trim(),
      });
      setSubmitted(true);
    } catch {
      setErr("We couldn't submit that just now. Please try again, or text us at (470) 823-8816.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <HomeownerGate {...auth} title="Refer a Neighbor">
      {deal && (
        <div className="space-y-4">
          {submitted ? (
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="text-5xl mb-3">🎉</div>
              <h1 className="text-xl font-bold text-bardin-ink mb-2">Referral Submitted!</h1>
              <p className="text-gray-600 mb-4">
                Thank you, {deal.homeowner_name?.split(" ")[0]}! We'll reach out to{" "}
                {name.split(" ")[0]} soon.
              </p>
              <div className="bg-bardin-sky rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-bardin-teal">
                  $250 Referral Reward
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  You'll receive $250 when your referral's roof is built.
                  We'll keep you updated!
                </p>
              </div>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setName("");
                  setPhone("");
                  setAddress("");
                  setCity("");
                  setNotes("");
                }}
                className="text-bardin-teal text-sm font-medium"
              >
                Refer Someone Else
              </button>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <h1 className="text-lg font-bold text-bardin-ink mb-1">
                  Refer a Friend or Neighbor
                </h1>
                <p className="text-sm text-gray-500 mb-2">
                  Know someone with a roof? Send them our way — we'll take a look and let them know where they stand. You earn{" "}
                  <span className="font-bold text-bardin-orange">$250</span> when their
                  roof is built.
                </p>

                <div className="bg-bardin-sky rounded-lg p-3 mb-4 flex items-center gap-3">
                  <span className="text-2xl">💰</span>
                  <div>
                    <p className="text-sm font-semibold text-bardin-teal">$250 per referral</p>
                    <p className="text-xs text-gray-600">Paid after their build is complete</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-bardin-ink mb-1">
                      Their Name *
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                      placeholder="First and last name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-bardin-ink mb-1">
                      Their Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-bardin-ink mb-1">
                      Their Address
                    </label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                      placeholder="Street address"
                    />
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                      placeholder="City"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Helps us get to the right roof — and makes sure you get credit.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-bardin-ink mb-1">
                      Anything we should know?
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent resize-none"
                      rows={2}
                      placeholder="e.g. best time to reach them"
                    />
                  </div>
                  {err && (
                    <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || !name.trim() || !phone.trim()}
                    className="w-full bg-bardin-orange text-white font-semibold rounded-lg py-3 hover:bg-bardin-orange/90 transition disabled:opacity-50"
                  >
                    {submitting ? "Submitting..." : "Submit Referral"}
                  </button>
                </form>
              </div>

              <div className="text-center text-sm text-gray-400 pb-6">
                You can also text referrals to{" "}
                <a href="sms:+14708238816" className="text-bardin-teal font-medium">
                  (470) 823-8816
                </a>
              </div>
            </>
          )}
        </div>
      )}
    </HomeownerGate>
  );
}
